import { useCallback, useEffect, useState } from "react";

import { themeVar as colors } from "../design-tokens";
import { AppShell } from "../ui/AppShell";
import { PageHead } from "../ui/PageHead";
import { blockStyle } from "../ui/Block";
import { useDocumentTitle, vbTitle } from "../ui/useDocumentTitle";
import {
  NOT_OFFERED_LINE, errorLine, formatWhen, getUsage, isForbidden, isNotOffered, thisMonth,
  usageCsvHref, type MlsUsageReportRow,
} from "./api";
import { MANAGERS_ONLY_LINE } from "./MlsAdminScreen";
import { fieldStyle, ghostButton } from "./styles";

/**
 * `/mls-access/usage` — "NTREIS usage", for the two group managers (Release 1.1 Slice A).
 *
 * One row per agent for the chosen month, from the append-only usage ledger: who they are, when
 * they first connected and activated, whether they were a member that month, and how many MLS
 * searches, photo fetches and MLS-backed reports they ran. COUNTS ONLY: there is no rate, no fee
 * and no dollar figure here, because the NTREIS fee terms are not in ValuBrief and what NTREIS
 * counts as "connected" versus "used" is the owner's call, so both are shown. An unknown value is
 * "—", never 0 or blank.
 */
type Load =
  | { kind: "loading" }
  | { kind: "off" }
  | { kind: "forbidden" }
  | { kind: "error"; message: string }
  | { kind: "ready"; rows: MlsUsageReportRow[] };

const dash = (v: string | null | undefined) => (v ? v : "—");
const when = (v: string | null) => formatWhen(v) ?? "—";

export function NtreisUsageScreen() {
  useDocumentTitle(vbTitle("NTREIS usage"));
  const [month, setMonth] = useState(thisMonth());
  const [load, setLoad] = useState<Load>({ kind: "loading" });

  const refresh = useCallback(async (m: string) => {
    setLoad({ kind: "loading" });
    try {
      setLoad({ kind: "ready", rows: (await getUsage(m)).rows });
    } catch (e) {
      if (isNotOffered(e)) setLoad({ kind: "off" });
      else if (isForbidden(e)) setLoad({ kind: "forbidden" });
      else setLoad({ kind: "error", message: errorLine(e, "We couldn't load the usage just now.") });
    }
  }, []);
  useEffect(() => { void refresh(month); }, [refresh, month]);

  return (
    <AppShell step="mlsusage" content="data">
      <div style={{ ...blockStyle("data"), padding: "8px 0 48px" }}>
        <PageHead title="NTREIS usage"
                  sub="Per agent, per month, from the MLS usage ledger. Counts only: the NTREIS fee terms are not in ValuBrief." />
        {load.kind === "off" && <p>{NOT_OFFERED_LINE}</p>}
        {load.kind === "forbidden" && <p>{MANAGERS_ONLY_LINE}</p>}
        {load.kind !== "off" && load.kind !== "forbidden" && (
          <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap", margin: "8px 0 16px" }}>
            <label style={{ fontSize: 13, fontWeight: 600 }} htmlFor="ntreis-month">
              Month
              <input id="ntreis-month" type="month" value={month}
                     onChange={(e) => { if (e.target.value) setMonth(e.target.value); }}
                     style={{ ...fieldStyle, width: 180, display: "block" }} />
            </label>
            <a href={usageCsvHref(month)} download className="vb-btn" data-testid="ntreis-csv"
               style={{ ...ghostButton, textDecoration: "none" }}>Download CSV</a>
          </div>
        )}
        {load.kind === "loading" && <p style={{ color: colors.textSecondary }}>Loading…</p>}
        {load.kind === "error" && <p role="status">{load.message}</p>}
        {load.kind === "ready" && load.rows.length === 0 && (
          <p data-testid="ntreis-empty">No NTREIS members and no MLS use in this month.</p>
        )}
        {load.kind === "ready" && load.rows.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table data-testid="ntreis-usage" style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
              <thead>
                <tr>{["Agent", "License", "NTREIS member ID", "First connected", "Activated",
                      "Member this month", "MLS searches", "Photo fetches", "Reports using MLS",
                      "First use", "Last use"].map((h) => (
                  <th key={h} scope="col" style={{ textAlign: "left", padding: "6px 8px",
                      borderBottom: `1px solid ${colors.border}` }}>{h}</th>))}</tr>
              </thead>
              <tbody>
                {load.rows.map((r) => (
                  <tr key={r.user_id} data-testid={`ntreis-row-${r.user_id}`}>
                    <td style={{ padding: "6px 8px" }}>{dash(r.name)}<br />
                      <span style={{ color: colors.textSecondary }}>{dash(r.email)}</span></td>
                    <td style={{ padding: "6px 8px" }}>{dash(r.license)}</td>
                    <td style={{ padding: "6px 8px" }}>{dash(r.ntreis_member_id)}</td>
                    <td style={{ padding: "6px 8px" }}>{when(r.first_connected_at)}</td>
                    <td style={{ padding: "6px 8px" }}>{when(r.activated_at)}</td>
                    <td style={{ padding: "6px 8px" }}>{r.member_during_month ? "Yes" : "No"}</td>
                    <td style={{ padding: "6px 8px", textAlign: "right" }}>{r.mls_searches}</td>
                    <td style={{ padding: "6px 8px", textAlign: "right" }}>{r.mls_objects}</td>
                    <td style={{ padding: "6px 8px", textAlign: "right" }}>{r.reports_used_mls}</td>
                    <td style={{ padding: "6px 8px" }}>{when(r.first_use)}</td>
                    <td style={{ padding: "6px 8px" }}>{when(r.last_use)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
